import { z } from 'zod'
import { DiditOutcomeSchema } from '../enums'
import { IsoDateSchema, IsoDateTimeSchema, LatitudeSchema, LongitudeSchema, UuidSchema } from '../primitives'

/* ================================================================== browser GPS */

/**
 * One browser geolocation attempt (navigator.geolocation, with consent) — captured at registration and
 * at vote time when the organization has `collectLocation`. Denials are recorded too (useful for review).
 */
export const GpsReadingSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    lat: LatitudeSchema,
    lng: LongitudeSchema,
    /** metres (GeolocationCoordinates.accuracy) */
    accuracyM: z.number().min(0).nullable(),
    altitudeM: z.number().nullable().default(null),
    capturedAt: IsoDateTimeSchema,
  }),
  z.object({
    status: z.enum(['denied', 'unavailable', 'timeout', 'unsupported']),
    capturedAt: IsoDateTimeSchema,
  }),
])
export type GpsReading = z.infer<typeof GpsReadingSchema>

/* ================================================================== vote GPS history */

/**
 * One GPS entry appended to votes.gps per vote submission.
 * Stored as a JSONB array (like locality_history); one entry per submission.
 * Only created when the GpsReading status is 'ok'.
 */
export const VoteGpsEntrySchema = z.object({
  questionId: UuidSchema.nullable(),
  lat: LatitudeSchema,
  lng: LongitudeSchema,
  /** Metres (GeolocationCoordinates.accuracy). */
  accuracyM: z.number().min(0).nullable(),
  timestamp: IsoDateTimeSchema,
})
export type VoteGpsEntry = z.infer<typeof VoteGpsEntrySchema>

/* ================================================================== Didit summary */

const nstr = z.string().nullable()
const nnum = z.number().nullable()
const nbool = z.boolean().nullable()

/** Identity extracted by Didit OCR (id_verifications[0]). */
export const DiditExtractedIdentitySchema = z.object({
  firstName: nstr,
  lastName: nstr,
  fullName: nstr,
  dateOfBirth: IsoDateSchema.nullable(),
  /** Age reported by Didit (at verification time). */
  age: nnum,
  gender: nstr,
  /** ISO3 */
  nationality: nstr,
  /** ISO3 */
  issuingState: nstr,
  documentType: nstr,
  documentNumber: nstr,
  personalNumber: nstr,
  expirationDate: nstr,
  dateOfIssue: nstr,
  placeOfBirth: nstr,
  /** MRZ holder nationality (ISO3) when the document has an MRZ (PLAN v4 R7: citizenship resolver input). */
  mrzNationality: nstr.optional(),
})
export type DiditExtractedIdentity = z.infer<typeof DiditExtractedIdentitySchema>

/** Address printed on the ID card, geocoded by Didit (parsed_address / id_document.location). */
export const DiditDocumentAddressSchema = z.object({
  raw: nstr,
  formatted: nstr,
  street: nstr,
  city: nstr,
  region: nstr,
  postalCode: nstr,
  country: nstr,
  lat: LatitudeSchema.nullable(),
  lng: LongitudeSchema.nullable(),
  isVerified: nbool,
})
export type DiditDocumentAddress = z.infer<typeof DiditDocumentAddressSchema>

/**
 * Proof of address (poa_verifications[0], PLAN v4 §2): the address read from the document and geocoded by Didit
 * (`poa_parsed_address.document_location`). The Didit sandbox returns a fixed USA mock without coordinates.
 */
export const DiditPoaSchema = z.object({
  status: nstr,
  documentType: nstr,
  issuer: nstr,
  issueDate: nstr,
  issuingState: nstr,
  address: nstr,
  formatted: nstr,
  city: nstr,
  region: nstr,
  postalCode: nstr,
  /** ISO2 as Didit reports it. */
  country: nstr,
  lat: LatitudeSchema.nullable(),
  lng: LongitudeSchema.nullable(),
  nameOnDocument: nstr,
})
export type DiditPoa = z.infer<typeof DiditPoaSchema>

/** Live-app second-pass Face Search at decision time (PLAN v4 R4): best effort, never a proof of uniqueness. */
export const DiditFaceSearchSchema = z.object({
  at: IsoDateTimeSchema,
  /** `ok` = searched; `skipped` = no reference image; `failed` = Didit error (ignored). */
  result: z.enum(['ok', 'skipped', 'failed']),
  /** Same-organization members whose face matched (self excluded). */
  memberIds: z.array(z.number().int()),
  /** Matches in other organizations / unknown sessions (count only, never their identity). */
  foreignMatches: z.number().int().optional(),
  /** Matches on retained face templates that cannot be attributed (legacy `member:<id>` vendor_data): review. */
  unresolvedMatches: z.number().int().optional(),
})
export type DiditFaceSearch = z.infer<typeof DiditFaceSearchSchema>

/** ip_analyses[0] geolocation + network flags. */
export const DiditIpGeoSchema = z.object({
  ip: nstr,
  city: nstr,
  /** ip_state */
  region: nstr,
  country: nstr,
  countryCode: nstr,
  lat: LatitudeSchema.nullable(),
  lng: LongitudeSchema.nullable(),
  isp: nstr,
  organization: nstr,
  /** is_vpn_or_tor */
  vpn: nbool,
  /** is_data_center */
  datacenter: nbool,
  timezone: nstr,
})
export type DiditIpGeo = z.infer<typeof DiditIpGeoSchema>

export const DiditDeviceSchema = z.object({
  brand: nstr,
  model: nstr,
  browser: nstr,
  browserVersion: nstr,
  os: nstr,
  osVersion: nstr,
  /** desktop | mobile | tablet */
  platform: nstr,
  fingerprint: nstr,
  userAgent: nstr,
})
export type DiditDevice = z.infer<typeof DiditDeviceSchema>

/** Distances in km reported by Didit (ip_analyses[0].ip / id_document). */
export const DiditDistancesSchema = z.object({
  ipToDocumentKm: nnum,
  ipToPoaKm: nnum,
})

export const DiditCheckSchema = z.object({ status: nstr, score: nnum })

export const DiditWarningItemSchema = z.object({
  code: z.string(),
  feature: nstr,
  /** Didit log_type: error | warning | information */
  severity: nstr,
  description: nstr,
  /** Our classification (see DIDIT_WARNING_TABLE). */
  class: z.enum(['final', 'retry', 'review', 'info']),
})
export type DiditWarningItem = z.infer<typeof DiditWarningItemSchema>

/** A previous session / member that shares a face, document, email, phone, IP or device. */
export const DiditDuplicateMatchSchema = z.object({
  kind: z.enum(['face', 'document', 'email', 'phone', 'ip', 'device']),
  sessionId: nstr,
  vendorData: nstr,
  /** Our member, resolved by the matched Didit session id (kyc_sessions), never by vendor_data alone. */
  memberId: z.number().int().nullable(),
  /** True when the matched member belongs to the same organization (only these are policy-relevant). */
  sameOrganization: z.boolean(),
  similarity: nnum,
  status: nstr,
})
export type DiditDuplicateMatch = z.infer<typeof DiditDuplicateMatchSchema>

/** Structured digest of one Didit decision, stored per KYC session (kyc_sessions.summary). */
export const DiditSummarySchema = z.object({
  sessionId: z.string(),
  sessionNumber: nnum,
  /** Didit status verbatim (Approved, Declined, In Review, …). */
  status: z.string(),
  /** Our classification of status + warnings. */
  outcome: DiditOutcomeSchema,
  environment: nstr,
  features: z.array(z.string()),
  extracted: DiditExtractedIdentitySchema.nullable(),
  documentAddress: DiditDocumentAddressSchema.nullable(),
  /** Proof of address (PLAN v4; absent on summaries stored before v4). */
  poa: DiditPoaSchema.nullable().optional(),
  ipGeo: DiditIpGeoSchema.nullable(),
  distances: DiditDistancesSchema.nullable(),
  device: DiditDeviceSchema.nullable(),
  liveness: z.object({ status: nstr, method: nstr, score: nnum, ageEstimation: nnum }).nullable(),
  faceMatch: DiditCheckSchema.nullable(),
  email: z
    .object({
      status: nstr,
      email: nstr,
      isBreached: nbool,
      isDisposable: nbool,
      isUndeliverable: nbool,
      verifiedAt: nstr,
    })
    .nullable(),
  phone: z
    .object({
      status: nstr,
      number: nstr,
      countryCode: nstr,
      carrier: nstr,
      isDisposable: nbool,
      isVirtual: nbool,
      verifiedAt: nstr,
    })
    .nullable(),
  warnings: z.array(DiditWarningItemSchema),
  duplicates: z.array(DiditDuplicateMatchSchema),
  /**
   * Didit matches with sessions of OTHER organizations or unknown to us (deleted organizations, earlier databases):
   * count and best similarity per kind only, never their identity (privacy). Absent on summaries stored before.
   */
  foreignMatches: z
    .array(
      z.object({
        kind: DiditDuplicateMatchSchema.shape.kind,
        count: z.number().int(),
        maxSimilarity: nnum,
      })
    )
    .optional(),
  /**
   * Matches that cannot be attributed to an organization: a retained face template whose User has a legacy
   * `member:<id>` vendor_data (collides across database generations, never trusted). Count + best similarity per
   * kind; they keep the duplicate warning (review).
   */
  unresolvedMatches: z
    .array(
      z.object({
        kind: DiditDuplicateMatchSchema.shape.kind,
        count: z.number().int(),
        maxSimilarity: nnum,
      })
    )
    .optional(),
  /**
   * `nid:` dedupe hashes of the document read by Didit, kept when the summary is minimized after the final decision
   * (the document / personal numbers themselves are dropped).
   */
  documentKeys: z.array(z.string()).optional(),
  /** Personal data cut to the minimum after the final decision (services/privacy.ts minimizeSummary). */
  minimized: z.boolean().optional(),
  /** Didit duplicate warnings ignored because every match is in another organization (codes + foreign match count). */
  ignoredWarnings: z.array(z.object({ code: z.string(), foreignMatches: z.number().int() })).optional(),
  /**
   * Sandbox (test) verification and OCR was null: `extracted` was filled from the declared data. Only ever true for
   * sessions on the Didit sandbox app (test organizations, or every organization of a DIDIT_MODE=sandbox deployment).
   * Labeled "Test data (sandbox)" in the UI.
   */
  sandboxSynthetic: z.boolean(),
  /** Live-app second-pass Face Search (R4), when it ran. */
  faceSearch: DiditFaceSearchSchema.nullable().optional(),
  /** When the decision was fetched from Didit. */
  fetchedAt: IsoDateTimeSchema,
})
export type DiditSummary = z.infer<typeof DiditSummarySchema>
